import { Box, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "../../hooks/useQuery";
import { useEffect, useState } from "react";
import Error from "../Error";
import usePurchase from "../../hooks/usePurchase";
import Loading from "../Loading/Loading";
import Lottie from "lottie-react";
import SuccessLottie from "./success.json";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUserState } from "../../state/user";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";

/** Payment methods the checkout can come back from. */
const PAYMENT_METHOD = {
  MBWAY: "mbw",
  CARD: "cc",
  REFERENCE: "mb"
};

/*
 * Styles follow the app's current dialog idiom — see PatientVideoDialog for the
 * gradient header and ConfirmDialog for the action button. The older
 * components/Dialog/Dialog wrapper is deliberately not used here: its grey
 * header and 25px paper radius are the pre-redesign look.
 */
const paperSx = { borderRadius: "16px", overflow: "hidden" };

const headerSx = {
  background: semanticColors.gradient.surface,
  px: 3,
  py: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.5
};

const headerTitleSx = { ...typography.body.lBold, color: "#fff" };

const closeBtnSx = { color: "#fff", flexShrink: 0 };

const bodySx = {
  p: 3,
  bgcolor: semanticColors.surface.canvas,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2
};

const messageSx = {
  ...typography.body.m,
  color: semanticColors.text.secondary,
  textAlign: "center"
};

const planNameSx = {
  ...typography.heading.m,
  color: semanticColors.text.primary,
  textAlign: "center"
};

const confirmBtnSx = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "44px",
  borderRadius: "40px",
  border: "none",
  cursor: "pointer",
  background: semanticColors.gradient.surface,
  ...typography.body.mBold,
  color: semanticColors.text.inverse
};

export default function DialogEndPayment() {
  const { t } = useTranslation();
  const history = useHistory();
  const [open, setOpen] = useState(true);
  const query = useQuery();
  const callbackQuery = query.get("callback");
  const methodQuery = query.get("method");
  const transactionId = query.get("transactionId");
  const { data, loading, error } = usePurchase({
    transactionId
  });
  const { refreshUser } = useUserState();

  useEffect(() => {
    if (!open) {
      const queryParams = "";
      history.replace({
        search: queryParams
      });
    }
  }, [history, open]);

  // The balance in the menu and on the page behind this modal was read before
  // the purchase, so it is now one package out of date. Pull it again.
  const purchased = callbackQuery === "success" && !error && !loading;
  useEffect(() => {
    if (purchased) refreshUser?.();
  }, [purchased, refreshUser]);

  if (!callbackQuery && !transactionId) return <></>;

  // The checkout can answer "success" and the transaction still fail to load —
  // both end up here, and both mean there is nothing to celebrate.
  const succeeded = callbackQuery === "success" && !error;

  // A Multibanco reference is not a completed payment: the money only moves
  // once it is paid, so this one says "awaiting" rather than "done".
  const message = !succeeded
    ? t("cmds.purchaseError")
    : methodQuery === PAYMENT_METHOD.REFERENCE
    ? t("cmds.purchasePendingReference")
    : t("cmds.purchaseSuccess");

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: paperSx }}
    >
      <Box sx={headerSx}>
        <Typography sx={headerTitleSx}>{t("cmds.purchaseTitle")}</Typography>
        <IconButton onClick={() => setOpen(false)} sx={closeBtnSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={bodySx}>
        <Typography sx={messageSx}>{message}</Typography>

        {loading ? (
          <Loading />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "135px"
              }}
            >
              {succeeded ? <Lottie animationData={SuccessLottie} /> : <Error />}
            </Box>

            {/* Only a completed purchase has a plan to name. */}
            {succeeded && data?.planName && (
              <Typography sx={planNameSx}>{data.planName}</Typography>
            )}

            <Box
              component="button"
              onClick={() => setOpen(false)}
              sx={confirmBtnSx}
            >
              {t("cmds.close")}
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
}
